/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useRawScriptInclude } from '@/hooks/use-raw-script-include';

export interface OmnitureHeaderIncludeProps {
    omnitureEnabled?: boolean;
    omnitureHeaderInclude?: string | null;
    /** CSP nonce for inline scripts; null/undefined when security headers middleware is disabled. */
    nonce?: string | null;
}

/**
 * Injects the `omnitureHeaderInclude` site preference into `document.head`
 * — the Storefront Next equivalent of the raw `<isprint value="${...}"
 * encoding="off" />` block in the legacy `htmlhead.isml` template.
 *
 * The preference value is expected to be one or more `<script>` tags (e.g.
 * the Adobe Launch library `<script src async onload="...">` plus an inline
 * bootstrap call). Injection is imperative (`useRawScriptInclude`) rather
 * than a JSX `<script>` — see that hook's doc comment for why: JSX would
 * either silently hoist an `async src` script out of place or drop an inline
 * `onload` handler.
 *
 * Renders nothing (the include has no visible/JSX output) when Omniture
 * isn't enabled or no header include is configured.
 */
export function OmnitureHeaderInclude({ omnitureEnabled, omnitureHeaderInclude, nonce }: OmnitureHeaderIncludeProps) {
    useRawScriptInclude(omnitureEnabled ? omnitureHeaderInclude : null, nonce, () => document.head);
    return null;
}
